import React, { useState, useEffect, useContext, useRef } from 'react';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { UserContext } from "../../../context/UserContext";
import { getImageCacheKey, getImageUrl, invalidateImageCache } from '../../../lib/helpers';
import { isValidEmail } from '../../../lib/email';
import { DEFAULT_PHONE_COUNTRY } from '../../../lib/phone';
import CustomModal from '../../Elements/CustomModal';
import {
  DEFAULT_MEMBERSHIP_DURATIONS,
  findMatchingDurationPresetId,
  formatDateForApi,
  getConsumptionTypeValue,
  getProfileDateRaw,
  MembershipDurationEditor,
  parseDayFromApi,
} from '../../Elements/MembershipDateFields';

function OwnersModal({ active, handleModal, token, id, ownerData, setErrorMessage, onSuccess }) {
  const [tokenFromContext, , , venueID] = useContext(UserContext);
  const [localError, setLocalError] = useState('');
  // Owner creado pero el email de invitación NO salió: {email, temporaryPassword}.
  // Mientras esté seteado, el modal muestra el aviso y solo se cierra a mano.
  const [inviteWarning, setInviteWarning] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Ref para evitar resetear el formulario mientras el usuario lo rellena en modo creación
  const lastFormModeRef = useRef(null); // 'create' | 'edit' | 'closed'
  const lastLoadedEditIdRef = useRef(null);
  
  // Usar el token del prop si está disponible, sino del contexto
  const authToken = token || tokenFromContext;
  
  // Detectar modo edición
  const isEditMode = !!id && !!ownerData;
  
  // Fecha de hoy en formato YYYY-MM-DD para usar en validaciones
  const today = new Date().toISOString().split('T')[0];

  // Estado centralizado del formulario
  const [formData, setFormData] = useState({
    email: '',
    password: '', // Opcional - si no se proporciona, el backend genera una temporal
    confirmationPassword: '',
    first_name: '',
    last_name: '',
    national_id: '',
    birth_date: '',
    postal_code: '',
    city: '',
    address: '',
    phone: '',
    credit_balance: '',
    discount: 'Sin descuento',
    fee: '',
    max_monthly_consumption: '',
    consumption_type: '',
    guarantor_member: '',
  });

  const [inscripcionInput, setInscripcionInput] = useState('');
  const [datePickerInscripcion, setDatePickerInscripcion] = useState('');
  const [caducidadInput, setCaducidadInput] = useState('');
  const [datePickerCaducidad, setDatePickerCaducidad] = useState('');
  const [selectedDurationId, setSelectedDurationId] = useState(null);
  const [durations, setDurations] = useState(DEFAULT_MEMBERSHIP_DURATIONS);

  const [selectedShop, setSelectedShop] = useState([]);
  const [shop, setShop] = useState([]);
  const [image, setImage] = useState(null);
  const [currentImageUrl, setCurrentImageUrl] = useState(null);
  const blobUrlRef = useRef(null);
  const imageInputRef = useRef(null);

  // Función helper para formatear fechas a YYYY-MM-DD
  const formatDate = (dateString) => {
    if (!dateString) return null;
    
    // Si ya está en formato YYYY-MM-DD, devolverlo
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    
    // Intentar parsear la fecha
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return null; // Fecha inválida
    }
    
    // Formatear a YYYY-MM-DD
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    const fetchShops = async () => {
      if (!authToken) return;
      
      const requestOptions = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + authToken,
        },
      };
      try {
        const response = await fetch(`/v1/venues/all`, requestOptions);
        if (!response.ok) throw new Error("Could not fetch venues");
        const data = await response.json();
        setShop(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching venues:", error);
        if (setErrorMessage) {
          setErrorMessage('Error al obtener tiendas');
        }
      }
    };
    
    fetchShops();
  }, [authToken, setErrorMessage]);

  // Cargar datos del propietario cuando se abre en modo edición
  // Solo resetear cuando se cierra el modal o se cambia de modo (edit<->create)
  useEffect(() => {
    if (active && isEditMode && ownerData) {
      if (lastLoadedEditIdRef.current === id) return;
      lastLoadedEditIdRef.current = id;
      lastFormModeRef.current = 'edit';
      // Cargar datos del propietario en el formulario
      const rawFee = String(ownerData.profile?.fee ?? '').trim();
      let feeField = '';
      if (rawFee) {
        const compact = rawFee.replace(/\s/g, '');
        const legacy = /^0[,.]00€?$/.test(compact) || rawFee === '0.00 €' || rawFee === '0,00 €';
        feeField = legacy ? '' : rawFee;
      }
      setFormData({
        email: ownerData.email || '',
        password: '', // No cargar contraseña por seguridad
        confirmationPassword: '',
        first_name: ownerData.profile?.first_name || ownerData.first_name || ownerData.profile?.name || '',
        last_name: ownerData.profile?.last_name || ownerData.last_name || '',
        national_id: ownerData.profile?.national_id?.toString() || '',
        birth_date: formatDate(ownerData.profile?.birth_date) || '',
        postal_code: ownerData.profile?.postal_code?.toString() || '',
        city: ownerData.profile?.city || '',
        address: ownerData.profile?.address || '',
        phone: ownerData.profile?.phone?.toString() || '',
        credit_balance: ownerData.profile?.credit_balance?.toString() || '0',
        discount: ownerData.profile?.discount || 'Sin descuento',
        fee: feeField,
        max_monthly_consumption: ownerData.profile?.max_monthly_consumption != null
          ? String(ownerData.profile.max_monthly_consumption)
          : '',
        consumption_type: getConsumptionTypeValue(ownerData),
        guarantor_member: ownerData.profile?.guarantor_member || '',
      });
      
      const enr = parseDayFromApi(getProfileDateRaw(ownerData, 'enrollment_date'));
      const exp = parseDayFromApi(getProfileDateRaw(ownerData, 'expiration_date'));
      setInscripcionInput(enr.display);
      setDatePickerInscripcion(enr.iso);
      setCaducidadInput(exp.display);
      setDatePickerCaducidad(exp.iso);
      
      // Cargar tiendas asignadas
      if (ownerData.owned_venues && Array.isArray(ownerData.owned_venues)) {
        const venueIds = ownerData.owned_venues.map(v => {
          if (typeof v === 'string') return v;
          return v.id || v;
        });
        setSelectedShop(venueIds);
      }
      
      // Cargar imagen actual si existe
      if (ownerData.profile?.documents_path) {
        setCurrentImageUrl(getImageUrl(ownerData.profile.documents_path, {
          cacheKey: getImageCacheKey('owner', ownerData.id || id),
        }));
      }
      
      setLocalError('');
      setImage(null);
    } else if (!active) {
      // Limpiar solo cuando se cierra el modal
      lastFormModeRef.current = 'closed';
      lastLoadedEditIdRef.current = null;
      setFormData({
        email: '',
        password: '',
        confirmationPassword: '',
        first_name: '',
        last_name: '',
        national_id: '',
        birth_date: '',
        postal_code: '',
        city: '',
        address: '',
        phone: '',
        credit_balance: '',
        discount: 'Sin descuento',
        fee: '',
        max_monthly_consumption: '',
        consumption_type: '',
        guarantor_member: '',
      });
      setSelectedShop([]);
      setLocalError('');
      setImage(null);
      setCurrentImageUrl(null);
      setInscripcionInput('');
      setDatePickerInscripcion('');
      setCaducidadInput('');
      setDatePickerCaducidad('');
      setSelectedDurationId(null);
      setDurations(DEFAULT_MEMBERSHIP_DURATIONS);
    } else if (active && !id) {
      // Modo creación: solo resetear al abrir por primera vez, no en cada re-render
      if (lastFormModeRef.current !== 'create') {
        lastFormModeRef.current = 'create';
        setFormData({
          email: '',
          password: '',
          confirmationPassword: '',
          first_name: '',
          last_name: '',
          national_id: '',
          birth_date: '',
          postal_code: '',
          city: '',
          address: '',
          phone: '',
          credit_balance: '',
          discount: 'Sin descuento',
          fee: '',
          max_monthly_consumption: '',
          consumption_type: '',
          guarantor_member: '',
        });
        setSelectedShop([]);
        setLocalError('');
        setImage(null);
        if (blobUrlRef.current) { URL.revokeObjectURL(blobUrlRef.current); blobUrlRef.current = null; }
        setCurrentImageUrl(null);
        setInscripcionInput('');
        setDatePickerInscripcion('');
        setCaducidadInput('');
        setDatePickerCaducidad('');
        setSelectedDurationId(null);
        setDurations(DEFAULT_MEMBERSHIP_DURATIONS);
      }
    }
  }, [active, id, isEditMode, ownerData]);

  useEffect(() => {
    if (!active || !isEditMode || !authToken) return;
    let shopId = null;
    if (ownerData?.owned_venues?.length) {
      const v = ownerData.owned_venues[0];
      shopId = typeof v === 'string' ? v : v?.id;
    }
    if (!shopId && selectedShop.length) shopId = selectedShop[0];
    if (!shopId) shopId = venueID;
    if (!shopId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/v1/venues/${shopId}/membership-durations`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && Array.isArray(data) && data.length) setDurations(data);
      } catch {
        /* mantener DEFAULT_MEMBERSHIP_DURATIONS */
      }
    })();
    return () => { cancelled = true; };
  }, [active, isEditMode, authToken, ownerData, selectedShop, venueID]);

  useEffect(() => {
    if (!active || !isEditMode || !ownerData || !durations.length) return;
    const toDayIso = (raw) => {
      if (raw == null || raw === '') return '';
      const s = String(raw).split('T')[0];
      return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : '';
    };
    const enroll = toDayIso(getProfileDateRaw(ownerData, 'enrollment_date'));
    const expire = toDayIso(getProfileDateRaw(ownerData, 'expiration_date'));
    setSelectedDurationId(findMatchingDurationPresetId(enroll, expire, durations));
  }, [active, isEditMode, ownerData, durations]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
      const url = URL.createObjectURL(file);
      blobUrlRef.current = url;
      setCurrentImageUrl(url);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectShop = (e) => {
    const shopId = e.target.value;
    if (selectedShop.includes(shopId)) {
      setSelectedShop(prev => prev.filter(id => id !== shopId));
    } else {
      setSelectedShop(prev => [...prev, shopId]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');
    setIsSubmitting(true);
    
    // Validaciones
    if (!isValidEmail(formData.email)) {
      const errorMsg = 'Por favor, ingresa un email válido';
      setLocalError(errorMsg);
      if (setErrorMessage) setErrorMessage(errorMsg);
      setIsSubmitting(false);
      return;
    }
    
    // Validar contraseña solo si se proporciona (es opcional)
    if (formData.password || formData.confirmationPassword) {
      if (formData.password !== formData.confirmationPassword) {
        const errorMsg = 'Las contraseñas no coinciden';
        setLocalError(errorMsg);
        if (setErrorMessage) setErrorMessage(errorMsg);
        setIsSubmitting(false);
        return;
      }
      
      if (formData.password.length > 0 && formData.password.length < 6) {
        const errorMsg = 'La contraseña debe tener al menos 6 caracteres';
        setLocalError(errorMsg);
        if (setErrorMessage) setErrorMessage(errorMsg);
        setIsSubmitting(false);
        return;
      }
    }
    
    // Validar campos requeridos.
    // El DNI es OPCIONAL (decisión 2026-08-03): al crear sin DNI, el payload genera
    // uno sintético de 8 dígitos (mismo patrón que los owners invitados por email);
    // al editar, vacío conserva el DNI existente. Solo Nombre y Apellidos bloquean.
    if (!formData.first_name || !formData.last_name) {
      const errorMsg = 'Por favor, completa Nombre y Apellidos';
      setLocalError(errorMsg);
      if (setErrorMessage) setErrorMessage(errorMsg);
      setIsSubmitting(false);
      return;
    }

    if (isEditMode) {
      const enrIso = formatDateForApi(inscripcionInput);
      const expIso = formatDateForApi(caducidadInput);
      if (enrIso !== '2000-01-01' && expIso !== '2000-01-01' && expIso <= enrIso) {
        const errorMsg = 'La fecha de caducidad debe ser posterior a la inscripción.';
        setLocalError(errorMsg);
        if (setErrorMessage) setErrorMessage(errorMsg);
        setIsSubmitting(false);
        return;
      }
    }
    
    // Formatear fechas correctamente - usar datos existentes en modo edición
    const birthDate = formatDate(formData.birth_date) || (isEditMode && ownerData?.profile?.birth_date ? formatDate(ownerData.profile.birth_date) : "2000-01-01");
    const enrollFallback = (isEditMode && getProfileDateRaw(ownerData, 'enrollment_date') && formatDate(getProfileDateRaw(ownerData, 'enrollment_date'))) || today;
    const expireFallback = (isEditMode && getProfileDateRaw(ownerData, 'expiration_date') && formatDate(getProfileDateRaw(ownerData, 'expiration_date'))) || '2099-12-31';
    const parsedEnr = formatDateForApi(inscripcionInput);
    const parsedExp = formatDateForApi(caducidadInput);
    const enrollmentDate = isEditMode
      ? (parsedEnr !== '2000-01-01' ? parsedEnr : enrollFallback)
      : today;
    const expirationDate = isEditMode
      ? (parsedExp !== '2000-01-01' ? parsedExp : expireFallback)
      : '2099-12-31';
    
    // Preparar owned_venues como array de UUIDs (strings)
    const ownedVenues = selectedShop.length > 0 
      ? selectedShop.map(id => String(id))
      : (isEditMode && ownerData?.owned_venues 
          ? ownerData.owned_venues.map(v => {
              if (typeof v === 'string') return v;
              return String(v.id || v);
            })
          : []);
    
    const guarantorMemberTrimmed = (formData.guarantor_member || '').trim();

    // Construir payload según el modo
    const ownerPayload = {
      email: formData.email.trim(),
      ...(isEditMode ? {} : { password: formData.password || undefined }), // Solo incluir password en creación
      ...(isEditMode ? {} : { owned_venues: ownedVenues }), // owned_venues solo en creación
      profile: {
        first_name: formData.first_name || (isEditMode ? ownerData?.profile?.first_name : "Propietario"),
        last_name: formData.last_name || (isEditMode ? ownerData?.profile?.last_name : "Nuevo"),
        national_id: (formData.national_id || '').trim().toUpperCase()
          || (isEditMode
            ? String(ownerData?.profile?.national_id || '')
            : String(Math.floor(10000000 + Math.random() * 89999999))),
        birth_date: birthDate,
        postal_code: parseInt(formData.postal_code) || (isEditMode ? ownerData?.profile?.postal_code : 28001),
        city: formData.city || (isEditMode ? ownerData?.profile?.city : "Madrid"),
        address: formData.address ?? (isEditMode ? ownerData?.profile?.address : "") ?? "",
        phone: formData.phone ? formData.phone.toString() : (isEditMode ? (ownerData?.profile?.phone || "") : ""),
        credit_balance: parseFloat(formData.credit_balance) || (isEditMode ? (ownerData?.profile?.credit_balance || 0.0) : 0.0),
        enrollment_date: enrollmentDate,
        expiration_date: expirationDate,
        guarantor_member: guarantorMemberTrimmed || null,
        cost_line: isEditMode ? (ownerData?.profile?.cost_line || "Defecto") : "Defecto",
        ...(isEditMode
          ? {
              discount: formData.discount || 'Sin descuento',
              fee: formData.fee.trim() || '0.00 €',
              max_monthly_consumption: parseInt(formData.max_monthly_consumption, 10) || 0,
              consumption_type: {
                ludenos: formData.consumption_type === 'ludenos',
                terap: formData.consumption_type === 'terap',
              },
            }
          : {
              discount: 'Sin descuento',
              fee: '0.00 €',
              max_monthly_consumption: 0,
              consumption_type: { ludenos: false, terap: false },
            }),
      }
    };
    
    // Si estamos editando y hay owned_venues, incluirlos
    if (isEditMode && ownedVenues.length > 0) {
      ownerPayload.owned_venues = ownedVenues;
    }
    
    const formDataToSend = new FormData();
    // El backend espera 'new_owner' para POST y 'owner_data' para PUT
    const formFieldName = isEditMode ? 'owner_data' : 'new_owner';
    formDataToSend.append(formFieldName, JSON.stringify(ownerPayload));
    
    // Agregar imagen si se seleccionó (el backend espera 'image')
    if (image) {
      formDataToSend.append('image', image);
    }
    
    try {
      const endpoint = isEditMode ? `/v1/owners/${id}` : '/v1/owners';
      const method = isEditMode ? 'PUT' : 'POST';
      
      const response = await fetch(endpoint, {
        method: method,
        headers: { 
          Authorization: "Bearer " + authToken 
        },
        body: formDataToSend,
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: `Error al ${isEditMode ? 'actualizar' : 'crear'} propietario` }));
        
        // Manejar errores de validación de Pydantic que vienen como array
        let errorMsg = `Error al ${isEditMode ? 'actualizar' : 'crear'} propietario`;
        if (errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            // Es un array de errores de validación
            errorMsg = errorData.detail.map(err => {
              const field = err.loc ? err.loc.join('.') : 'campo';
              return `${field}: ${err.msg}`;
            }).join('; ');
          } else if (typeof errorData.detail === 'string') {
            errorMsg = errorData.detail;
          } else {
            errorMsg = JSON.stringify(errorData.detail);
          }
        }
        
        setLocalError(errorMsg);
        if (setErrorMessage) setErrorMessage(errorMsg);
        setIsSubmitting(false);
        return;
      }
      
      const data = await response.json();
      invalidateImageCache(getImageCacheKey('owner', data.id || id));

      // El backend cuenta si el email de invitación salió. Si NO salió, el modal
      // no puede cerrarse en silencio: el creador debe ver la contraseña temporal
      // (única forma de que el owner entre) antes de perderla para siempre.
      if (!isEditMode && data.invitation_email_sent === false) {
        setInviteWarning({
          email: data.email,
          temporaryPassword: data.temporary_password || null,
        });
      }

      // Limpiar formulario
      setFormData({
        email: '',
        password: '',
        confirmationPassword: '',
        first_name: '',
        last_name: '',
        national_id: '',
        birth_date: '',
        postal_code: '',
        city: '',
        address: '',
        phone: '',
        credit_balance: '',
        discount: 'Sin descuento',
        fee: '',
        max_monthly_consumption: '',
        consumption_type: '',
        guarantor_member: '',
      });
      setSelectedShop([]);
      setImage(null);
      setCurrentImageUrl(null);
      setLocalError('');
      setIsSubmitting(false);
      setInscripcionInput('');
      setDatePickerInscripcion('');
      setCaducidadInput('');
      setDatePickerCaducidad('');
      setSelectedDurationId(null);
      setDurations(DEFAULT_MEMBERSHIP_DURATIONS);
      
      // Notificar al componente padre que se creó/actualizó exitosamente
      if (onSuccess) {
        onSuccess(data);
      }

      // Cerrar modal - el componente padre debería recargar. Si el email de
      // invitación falló, se queda abierto mostrando el aviso (cierre manual).
      if (isEditMode || data.invitation_email_sent !== false) {
        handleModal();
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} owner:`, error);
      const errorMsg = error.message || `Ocurrió un error al ${isEditMode ? 'actualizar' : 'crear'} el propietario`;
      setLocalError(errorMsg);
      if (setErrorMessage) setErrorMessage(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <CustomModal
      active={active}
      handleModal={handleModal}
      title={isEditMode ? 'Editar Propietario' : 'Crear Propietario'}
      size="wide"
      titleId="owners-modal-title"
      footer={
        inviteWarning ? (
          <button
            type="button"
            className="wa-btn-primary"
            onClick={() => { setInviteWarning(null); handleModal(); }}
          >
            Entendido, cerrar
          </button>
        ) : (
        <>
          <button type="button" className="wa-btn-secondary" onClick={handleModal} disabled={isSubmitting}>
            Cancelar
          </button>
          <button
            type="submit"
            form="owners-modal-form"
            disabled={isSubmitting}
            className="wa-btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Guardando...' : (isEditMode ? 'Actualizar' : 'Registrar')}
          </button>
        </>
        )
      }
    >
      {inviteWarning ? (
        <div
          className="rounded-lg border border-amber-300 bg-amber-50 p-5 text-sm text-amber-900 space-y-3"
          role="alert"
        >
          <p className="font-semibold text-base">
            ⚠️ Propietario creado, pero el email de invitación NO se pudo enviar a {inviteWarning.email}.
          </p>
          {inviteWarning.temporaryPassword ? (
            <>
              <p>
                Esta es su contraseña temporal — cópiala ahora y hazla llegar al propietario.
                No se volverá a mostrar:
              </p>
              <div className="rounded-md border border-amber-300 bg-white px-4 py-3 font-mono text-lg tracking-wide select-all">
                {inviteWarning.temporaryPassword}
              </div>
            </>
          ) : (
            <p>
              El propietario puede recuperar el acceso con «¿Olvidaste la contraseña?»,
              o revisa la configuración SMTP del servidor y vuelve a intentarlo.
            </p>
          )}
        </div>
      ) : (
      <form id="owners-modal-form" onSubmit={handleSubmit} className="wa-form space-y-4" autoComplete="off">
        {localError && (
          <div className="wa-form-error relative pr-10" role="alert">
            <strong>Error: </strong>
            <span>{localError}</span>
            <button
              type="button"
              className="absolute top-2 right-2 text-red-800 hover:opacity-80"
              onClick={() => setLocalError('')}
              aria-label="Cerrar mensaje"
            >
              ×
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Columna izquierda */}
                <div className="space-y-6">
                  {/* Nombre, Apellidos */}
                  <div className="flex flex-wrap gap-4">
                    <div className="flex-1 min-w-[150px]">
                      <label className="block text-sm font-medium mb-1">Nombre *</label>
                      <input
                        type="text"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleChange}
                        placeholder="nombre"
                        className="wa-input"
                        required
                      />
                    </div>
                    <div className="flex-1 min-w-[150px]">
                      <label className="block text-sm font-medium mb-1">Apellidos *</label>
                      <input
                        type="text"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleChange}
                        placeholder="apellidos"
                        className="wa-input"
                        required
                      />
                    </div>
                  </div>

                  {/* DNI, Fecha Nacimiento, CP, Ciudad */}
                  <div className="flex flex-wrap gap-4">
                    <div className="flex-1 min-w-[150px]">
                      <label className="block text-sm font-medium mb-1">DNI / NIE (opcional)</label>
                      <input
                        type="text"
                        name="national_id"
                        value={formData.national_id}
                        onChange={(e) => setFormData((prev) => ({ ...prev, national_id: e.target.value }))}
                        placeholder="12345678A"
                        className="wa-input"
                        inputMode="text"
                        autoCapitalize="characters"
                        autoComplete="off"
                        spellCheck={false}
                      />
                    </div>
                    <div className="flex-1 min-w-[150px]">
                      <label className="block text-sm font-medium mb-1">Fecha Nacimiento</label>
                      <input
                        type="date"
                        name="birth_date"
                        value={formData.birth_date}
                        onChange={handleChange}
                        className="wa-input"
                        max={today}
                      />
                    </div>
                    <div className="flex-1 min-w-[100px]">
                      <label className="block text-sm font-medium mb-1">CP</label>
                      <input
                        type="text"
                        name="postal_code"
                        value={formData.postal_code}
                        onChange={(e) => {
                          // Permitir solo números para código postal
                          const value = e.target.value.replace(/\D/g, '');
                          setFormData(prev => ({
                            ...prev,
                            postal_code: value
                          }));
                        }}
                        placeholder="28001"
                        maxLength={5}
                        className="wa-input"
                      />
                    </div>
                    <div className="flex-1 min-w-[150px]">
                      <label className="block text-sm font-medium mb-1">Ciudad</label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        placeholder="ciudad"
                        className="wa-input"
                      />
                    </div>
                  </div>

                  {/* Dirección */}
                  <div className="flex flex-wrap gap-4">
                    <div className="w-full">
                      <label className="block text-sm font-medium mb-1">Dirección</label>
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        placeholder="dirección"
                        className="wa-input"
                      />
                    </div>
                  </div>

                  <div className="wa-form-field w-full">
                    <label className="wa-label">Socio avalado</label>
                    <input
                      type="text"
                      name="guarantor_member"
                      value={formData.guarantor_member}
                      onChange={handleChange}
                      placeholder="socio avalado"
                      className="wa-input"
                    />
                  </div>

                  {isEditMode && (
                  <>
                  {/* Descuento, cuota, consumo — mismo criterio que editar socio */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full">
                    <div className="wa-form-field min-w-0">
                      <label className="wa-label">Descuento</label>
                      <select name="discount" value={formData.discount} onChange={handleChange} className="wa-select w-full">
                        <option>Sin descuento</option>
                        <option>5%</option>
                        <option>10%</option>
                        <option>15%</option>
                        <option>20%</option>
                        <option>50%</option>
                        <option>100%</option>
                      </select>
                    </div>
                    <div className="wa-form-field min-w-0">
                      <label className="wa-label">Cuota</label>
                      <input
                        type="text"
                        name="fee"
                        value={formData.fee}
                        onChange={handleChange}
                        placeholder="ej: Mensual"
                        className="wa-input"
                      />
                    </div>
                    <div className="wa-form-field min-w-0">
                      <label className="wa-label">Consumo máx/mes</label>
                      <input
                        type="number"
                        name="max_monthly_consumption"
                        value={formData.max_monthly_consumption}
                        onChange={handleChange}
                        placeholder="0"
                        min="0"
                        className="wa-input"
                      />
                    </div>
                    <div className="wa-form-field min-w-0">
                      <span className="wa-label">Tipo de consumo</span>
                      <div className="flex flex-wrap gap-4 items-center min-h-[40px]">
                        <label className="flex items-center gap-2 text-sm text-[var(--wa-foreground)] cursor-pointer">
                          <input
                            type="radio"
                            name="consumption_type_owner"
                            value="ludenos"
                            checked={formData.consumption_type === 'ludenos'}
                            onChange={() => setFormData((prev) => ({ ...prev, consumption_type: 'ludenos' }))}
                          />
                          Lúd.
                        </label>
                        <label className="flex items-center gap-2 text-sm text-[var(--wa-foreground)] cursor-pointer">
                          <input
                            type="radio"
                            name="consumption_type_owner"
                            value="terap"
                            checked={formData.consumption_type === 'terap'}
                            onChange={() => setFormData((prev) => ({ ...prev, consumption_type: 'terap' }))}
                          />
                          Terap.
                        </label>
                      </div>
                    </div>
                  </div>

                  <MembershipDurationEditor
                    durations={durations}
                    selectedDurationId={selectedDurationId}
                    setSelectedDurationId={setSelectedDurationId}
                    inscripcionInput={inscripcionInput}
                    setInscripcionInput={setInscripcionInput}
                    datePickerInscripcion={datePickerInscripcion}
                    setDatePickerInscripcion={setDatePickerInscripcion}
                    caducidadInput={caducidadInput}
                    setCaducidadInput={setCaducidadInput}
                    datePickerCaducidad={datePickerCaducidad}
                    setDatePickerCaducidad={setDatePickerCaducidad}
                  />
                  </>
                  )}

                  {/* Teléfono, Email */}
                  <div className="flex flex-wrap gap-4">
                    <div className="flex-1 min-w-[150px]">
                      <label className="block text-sm font-medium mb-1">Teléfono</label>
                      {/* Mismo selector de país que el alta de socio (CustomerModal):
                          entrega E.164 (+34...) que el backend valida y normaliza.
                          Opcional: vacío se acepta. El input crudo de antes sugería
                          "600000000" sin prefijo — justo lo que el create rechazaba. */}
                      <PhoneInput
                        international
                        defaultCountry={DEFAULT_PHONE_COUNTRY}
                        value={formData.phone || undefined}
                        onChange={(value) => setFormData(prev => ({ ...prev, phone: value || '' }))}
                        placeholder="Teléfono (opcional)"
                        className="wa-phone-input"
                      />
                    </div>
                    <div className="flex-1 min-w-[150px]">
                      <label className="block text-sm font-medium mb-1">Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="email@ejemplo.com"
                        className="wa-input"
                        autoComplete="off"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Columna derecha */}
                <div className="space-y-6">
                  {/* Contraseñas - Solo en modo creación */}
                  {!isEditMode && (
                    <>
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Contraseña (Opcional)
                        </label>
                        <input
                          type="password"
                          name="password"
                          value={formData.password}
                          onChange={handleChange}
                          className="wa-input"
                          placeholder="Dejar vacío para generar automáticamente"
                          autoComplete="new-password"
                          minLength={6}
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Si no se proporciona, se generará una contraseña temporal automáticamente
                        </p>
                      </div>

                      {formData.password && (
                        <div>
                          <label className="block text-sm font-medium mb-1">Confirmar Contraseña</label>
                          <input
                            type="password"
                            name="confirmationPassword"
                            value={formData.confirmationPassword}
                            onChange={handleChange}
                            className="wa-input"
                            autoComplete="new-password"
                            minLength={6}
                          />
                        </div>
                      )}
                    </>
                  )}

                  {/* Saldo inicial */}
                  <div>
                    <label className="block text-sm font-medium mb-1">Saldo Inicial</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      name="credit_balance"
                      value={formData.credit_balance}
                      onChange={handleChange}
                      placeholder="0.00"
                      className="wa-input"
                    />
                  </div>

                  {/* Tiendas opcionales para owners */}
                  <div>
                    <label htmlFor="shop_owner" className="block text-sm font-medium mb-1">
                      Tiendas Asignadas
                    </label>
                    <div className="mt-2 max-h-[200px] overflow-y-auto space-y-2">
                      {shop.map((shopItem) => (
                        <label key={shopItem.id} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            value={shopItem.id}
                            checked={selectedShop.includes(shopItem.id)}
                            onChange={handleSelectShop}
                            className="h-5 w-5"
                          />
                          <span>{shopItem.name}</span>
                        </label>
                      ))}
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Opcional. Puedes crear el propietario sin tienda y asignarla despues.
                    </p>
                  </div>

                  {/* Campo para subir imagen */}
                  <div className="max-md:rounded-xl max-md:border max-md:border-dashed max-md:border-lime-300 max-md:bg-lime-50/50 max-md:p-4">
                    <label htmlFor="image" className="mb-1 block text-sm font-medium max-md:text-center">
                      Imagen de Perfil <span className="text-xs font-normal text-gray-400">(opcional)</span>
                    </label>
                    <div className="mt-1 flex items-center space-x-4 max-md:flex-col max-md:items-center max-md:gap-4 max-md:space-x-0">
                      <div className="shrink-0">
                        {currentImageUrl ? (
                          <img
                            src={currentImageUrl}
                            alt="Vista previa"
                            className="h-28 w-28 rounded-full border border-gray-300 object-cover max-md:h-24 max-md:w-24"
                          />
                        ) : (
                          <div className="flex h-28 w-28 items-center justify-center rounded-full border border-gray-300 bg-gray-200 max-md:h-24 max-md:w-24">
                            <svg className="h-8 w-8 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1 max-md:w-full">
                        <input
                          ref={imageInputRef}
                          type="file"
                          id="image"
                          name="image"
                          accept="image/*"
                          onChange={handleImageChange}
                          className="wa-file-input block w-full min-w-0 max-md:sr-only"
                        />
                        <button
                          type="button"
                          onClick={() => imageInputRef.current?.click()}
                          className="hidden w-full items-center justify-center rounded-lg border border-lime-500 bg-lime-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-lime-700 max-md:inline-flex"
                        >
                          {image || currentImageUrl ? "Cambiar imagen" : "Seleccionar archivo"}
                        </button>
                        {image?.name ? (
                          <p className="mt-2 hidden truncate text-center text-xs font-medium text-emerald-700 max-md:block">
                            {image.name}
                          </p>
                        ) : null}
                        <p className="mt-1 text-xs text-gray-500 max-md:mt-2 max-md:text-center">
                          Selecciona una imagen JPG, PNG o GIF (máximo 5MB). Puedes dejarlo vacío.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
      </form>
      )}
    </CustomModal>
  );
}

export default OwnersModal;
